import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Search, 
  Bell, 
  ChevronDown, 
  Edit, 
  Copy, 
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Info,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Shirt,
  User,
  Settings,
  Heart,
  Image as ImageIcon,
  Box,
  Tag,
  Eye,
  Calendar,
  Award,
  Star,
  Check,
  Share2,
  Camera,
  Upload,
  Globe,
  Instagram,
  ExternalLink,
  Users,
  Wallet,
  Package,
  X,
  Clock
} from 'lucide-react';
import ModelPreview from '../components/ModelPreview';
import ModelViewer from '../components/ModelViewer';
import VerifiedBadge from '../components/VerifiedBadge';
import { normalizePieces } from '../utils/pieceNormalizer';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import PieceCard from '../components/PieceCard';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { completePurchaseWithDelegate, checkSufficientBalance } from '../utils/marketplaceTransactions';
import { createOffer } from '../utils/offersContract';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { useWalletFavorites } from '../hooks/useWalletFavorites';
import { useFavorites } from '../hooks/useFavorites';
import { useVerification } from '../contexts/VerificationContext';
import { useWalletAction } from '../hooks/useWalletAction';
import WalletConnectionModal from '../components/WalletConnectionModal';
import VerificationNotification from '../components/VerificationNotification';
import RequestTradeModal from '../components/RequestTradeModal';
import { 
  getOrCreateCreatorProfile, 
  updateCreatorProfile, 
  getCreatorProfileByHandle,
  uploadProfileImage,
  deleteProfileImage
} from '../utils/profileManagement';
import { getAllCapsules, getUserDrafts, getPieceById, getPiecesByHolder } from '../utils/supabaseDatabase';
import { getMarketplacePiecesByCreator } from '../utils/marketplaceQueries';
import { supabase } from '../lib/supabase';
import { fetchWalletNFTs } from '../utils/marketplaceTransactions';
import { trackCreatorView } from '../utils/creatorViewTracking';
import WalletRequired from '../components/WalletRequired';
import { tradeEvents } from '../utils/tradeEvents';
import { useToast, ToastContainer } from '../components/Toast';
import { createGroupedPieceCards, isGroupedCard, getAvailablePieceFromGroup } from '../utils/pieceGrouping';
import GroupedPiecesModal from '../components/GroupedPiecesModal';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { toasts, removeToast, toast } = useToast();
  const { toggleWalletFavorite, isWalletFavorited } = useWalletFavorites();
  const { toggleFavorite, isFavorited } = useFavorites(); // For piece favorites
  const { submitVerificationRequest, isLoading: verificationLoading, userVerificationStatus } = useVerification();
  const { withWalletCheck, showWalletModal, walletModalMessage, closeWalletModal } = useWalletAction();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const [user, setUser] = useState(null);
  const [capsules, setCapsules] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [createdPieces, setCreatedPieces] = useState([]);
  const [ownedPieces, setOwnedPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  // Progressive loading states for better UX
  const [capsulesLoading, setCapsulesLoading] = useState(true);
  const [createdPiecesLoading, setCreatedPiecesLoading] = useState(true);
  const [ownedPiecesLoading, setOwnedPiecesLoading] = useState(true);
  const [currentView, setCurrentView] = useState('created');
  const [searchTerm, setSearchTerm] = useState('');
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false); // For sliding activity panel
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [notForSaleModal, setNotForSaleModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({ verified: false, status: 'not_requested' });
  const [showVerificationNotification, setShowVerificationNotification] = useState(false);
  const [sortBy, setSortBy] = useState('price-low');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [editForm, setEditForm] = useState({
    username: '',
    bio: '',
    profilePicture: null,
    banner: null,
    socialLinks: {
      twitter: '',
      instagram: '',
      website: ''
    }
  });

  // Helper function to create a File object from base64 data
  const createFileFromDataUrl = (dataUrl, fileName, fileType) => {
    if (!dataUrl) return null;
    
    try {
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], fileName, { type: fileType });
      
      return file;
    } catch (error) {
      console.error('Error creating file from data URL:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadUserData();
    };
    loadData();
  }, [username, isConnected, publicKey]); // Removed balance to prevent reloading on every balance change

  // Listen for trade events and reload pieces when trades complete
  useEffect(() => {
    console.log('📢 Profile: Setting up trade event listener');
    const unsubscribe = tradeEvents.subscribe((tradeData) => {
      console.log('📢 Profile: Trade event received, reloading user data...', tradeData);
      loadUserData();
    });

    return () => {
      console.log('📢 Profile: Cleaning up trade event listener');
      unsubscribe();
    };
  }, []);

  // Load verification status for current user
  useEffect(() => {
    if (!username && isConnected && publicKey) {
      // Get status from context state
      const status = userVerificationStatus[publicKey.toString()] || {
        verified: false,
        status: 'not_requested',
        adminNotes: '',
        updatedAt: null
      };
      setVerificationStatus(status);
      
      // Show notification if status changed
      if (status.status !== 'not_requested' && status.updatedAt) {
        setShowVerificationNotification(true);
      }
    }
  }, [username, isConnected, publicKey, userVerificationStatus]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort options
  const sortOptions = [
    { id: 'price-low', name: 'Price low to high' },
    { id: 'price-high', name: 'Price high to low' },
    { id: 'recent', name: 'Recently created' },
    { id: 'sold', name: 'Recently sold' }
  ];

  // Handle dropdown
  const handleSortChange = (optionId) => {
    console.log(`🔄 Changing sort from "${sortBy}" to "${optionId}" on "${currentView}" tab`);
    setSortBy(optionId);
    setIsDropdownOpen(false);
  };

  // Load capsules from Supabase
  const loadCapsules = async (walletAddress) => {
    try {
      if (!walletAddress) return []; // Need a wallet address to filter capsules
      
      const capsulesResponse = await getAllCapsules();
      console.log('Loaded capsules from Supabase:', capsulesResponse);
      
      // Extract capsules array from response
      const capsulesData = capsulesResponse.capsules || [];
      
      // Filter capsules by provided wallet address (creator)
      console.log('Filtering capsules for wallet:', walletAddress);
      console.log('All capsules data:', capsulesData);
      console.log('Capsules with creators:', capsulesData.map(c => ({ 
        id: c.id, 
        name: c.name, 
        creator: c.creators?.wallet 
      })));
      
      const userCapsules = capsulesData.filter(capsule => 
        capsule.creators && capsule.creators.wallet === walletAddress
      );
      
      console.log('Filtered user capsules:', userCapsules);
      
      // Transform capsules to match our data structure
      const transformedCapsules = userCapsules.map(capsule => ({
        id: capsule.id,
        name: capsule.name,
        description: capsule.description,
        banner: capsule.banner || capsule.banner_url,
        image: capsule.banner || capsule.banner_url || capsule.image, // fallback for banner
        createdDate: capsule.created_at,
        createdAt: capsule.created_at,
        dropDate: capsule.dropped_at || capsule.created_at, // Use dropped_at, fallback to created_at
        collectionModels: capsule.pieces || [],
        pieceCount: capsule.pieceCount || capsule.piece_count || 0,
        views: Math.floor(Math.random() * 2000) + 100,
        likes: Math.floor(Math.random() * 150) + 10,
        downloads: Math.floor(Math.random() * 50) + 5,
        price: `${(capsule.pieceCount || capsule.piece_count || 0) * 2.5} SOL`,
        avgPrice: capsule.avgPrice || `${(capsule.pieceCount || capsule.piece_count || 0) * 2.5}`,
        status: capsule.status || 'published',
        type: 'capsule'
      }));
      
      console.log('Transformed user capsules:', transformedCapsules);
      return transformedCapsules;
    } catch (error) {
      console.error('Error loading capsules:', error);
      return [];
    }
  };

  // OPTIMIZED: Load basic profile data first (fast)
  const loadBasicProfile = async () => {
    try {
      setLoading(true);
      let userData;
      
      // If no username provided, this is the current user's profile
      if (!username) {
        if (!isConnected || !publicKey) {
          // User not connected, don't set user data (WalletRequired will be shown)
          setUser(null);
          setLoading(false);
          return null;
        } else {
          // User is connected, get or create profile from Supabase
          const walletAddress = publicKey.toString();
          const profileResult = await getOrCreateCreatorProfile(walletAddress);
          
          // Handle the StandardResponse format
          if (!profileResult.success) {
            console.error('❌ Failed to load profile:', profileResult.error);
            toast.error('Failed to load profile. Please refresh the page.');
            setLoading(false);
            return null;
          }
          
          const creatorProfile = profileResult.data;
          
          userData = {
            id: 'current-user',
            address: `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
            fullAddress: walletAddress,
            username: creatorProfile.handle || `User_${walletAddress.slice(0, 6)}`,
            bio: creatorProfile.bio || 'Digital fashion enthusiast and creator',
            joinDate: creatorProfile.created_at || new Date().toISOString(),
            balance: balance,
            profilePicture: creatorProfile.profile_picture_url || null,
            banner: creatorProfile.banner_url || null,
            verified: creatorProfile.is_verified || verificationStatus.verified,
            verificationType: creatorProfile.verification_type || 'none',
            socialLinks: creatorProfile.social_links || {},
            stats: {
              totalItems: 0,
              totalValue: 0,
              itemsCreated: 0,
              favorites: 0,
              outfits: 0,
              totalCapsules: 0,
              totalPieces: 0,
              totalViews: 0,
              totalLikes: 0
            }
          };
        }
      } else {
        // Username provided, look up user by handle
        const profileResult = await getCreatorProfileByHandle(username);
        
        if (profileResult.success && profileResult.data) {
          const creatorProfile = profileResult.data;
          
          // Track creator view (don't track if viewing own profile)
          const isOwnProfile = isConnected && publicKey && 
            (creatorProfile.wallet === publicKey.toString() || 
             creatorProfile.handle === `user_${publicKey.toString().slice(0, 8)}`);
          
          if (!isOwnProfile) {
            await trackCreatorView(username, 'handle');
          }
          
          userData = {
            id: creatorProfile.id,
            address: `${creatorProfile.wallet.slice(0, 6)}...${creatorProfile.wallet.slice(-4)}`,
            fullAddress: creatorProfile.wallet,
            username: creatorProfile.handle,
            bio: creatorProfile.bio || 'Digital fashion enthusiast',
            joinDate: creatorProfile.created_at,
            balance: 0,
            profilePicture: creatorProfile.profile_picture_url || null,
            banner: creatorProfile.banner_url || null,
            verified: creatorProfile.is_verified,
            verificationType: creatorProfile.verification_type || 'none',
            socialLinks: creatorProfile.social_links || {},
            stats: {
              totalItems: 0,
              totalValue: 0,
              itemsCreated: 0,
              favorites: 0,
              outfits: 0,
              totalCapsules: 0,
              totalPieces: creatorProfile.total_pieces_created || 0,
              totalViews: creatorProfile.total_views || 0,
              totalLikes: 0
            }
          };
        } else {
          // Fallback if profile not found
          userData = {
            id: username,
            address: `${username.slice(0, 6)}...${username.slice(-4)}`,
            fullAddress: username,
            username: username.charAt(0).toUpperCase() + username.slice(1),
            bio: 'Digital fashion enthusiast',
            joinDate: new Date().toISOString(),
            balance: 0,
            profilePicture: null,
            banner: null,
            verified: false,
            verificationType: 'none',
            socialLinks: {},
            stats: {
              totalItems: 0,
              totalValue: 0,
              itemsCreated: 0,
              favorites: 0,
              outfits: 0,
              totalCapsules: 0,
              totalPieces: 0,
              totalViews: 0,
              totalLikes: 0
            }
          };
        }
      }
      
      setUser(userData);
      setLoading(false); // Profile loaded - show it immediately!
      return userData;
    } catch (error) {
      console.error('Error loading profile:', error);
      setLoading(false);
      return null;
    }
  };

  // OPTIMIZED: Load capsules independently
  const loadCapsulesData = async (userData) => {
    if (!userData) return;
    
    try {
      setCapsulesLoading(true);
      const profileWallet = userData.fullAddress;
      const currentUserWallet = publicKey?.toBase58();
      const isOwnProfile = !username || (profileWallet === currentUserWallet);

      const supabaseCapsules = await loadCapsules(userData.fullAddress);
      
      // Only load draft capsules if viewing own profile
      let draftCapsules = [];
      if (isOwnProfile) {
        const drafts = JSON.parse(localStorage.getItem('drafts') || '[]');
        draftCapsules = drafts.length > 0 ? drafts
          .filter(draft => draft.collectionModels && Array.isArray(draft.collectionModels))
          .map(draft => ({
            id: draft.id,
            name: draft.name,
            description: draft.description,
            collectionModels: draft.collectionModels,
            createdDate: draft.createdAt,
            createdAt: draft.createdAt,
            views: Math.floor(Math.random() * 2000) + 100,
            likes: Math.floor(Math.random() * 150) + 10,
            downloads: Math.floor(Math.random() * 50) + 5,
            price: `${draft.collectionModels.length * 2.5} SOL`,
            status: 'draft',
            type: 'capsule'
          })) : [];
      }
      
      // Combine Supabase capsules with draft capsules (only for own profile)
      const userCapsules = [...supabaseCapsules, ...draftCapsules];
      setCapsules(userCapsules);
      setCapsulesLoading(false);
    } catch (error) {
      console.error('Error loading capsules:', error);
      setCapsules([]);
      setCapsulesLoading(false);
    }
  };

  // OPTIMIZED: Load created pieces independently
  const loadCreatedPiecesData = async (userData) => {
    if (!userData) return;
    
    try {
      setCreatedPiecesLoading(true);
      const profileWallet = userData.fullAddress;
      const currentUserWallet = publicKey?.toBase58();
      const isOwnProfile = !username || (profileWallet === currentUserWallet);
      
      console.log('🎨 Loading created pieces for wallet:', profileWallet);
      console.log('🔍 Is own profile:', isOwnProfile);
      
      // Get both listed and draft pieces (edition-aware - only masters)
      // Load up to 100 pieces for complete display
      // Note: Only load drafts if viewing own profile
      const promises = [
        getMarketplacePiecesByCreator(profileWallet, 0, 100)
      ];
      
      // Only include drafts if viewing own profile
      if (isOwnProfile) {
        promises.push(getUserDrafts(profileWallet, 0, 20));
      }
      
      const responses = await Promise.all(promises);
      const listedResponse = responses[0];
      const draftsResponse = isOwnProfile ? responses[1] : { pieces: [] };
      
      const allCreatedPieces = [
        ...(listedResponse.pieces || []),
        ...(draftsResponse.pieces || [])
      ];
      
      console.log('📦 Listed pieces:', listedResponse.pieces?.length || 0);
      console.log('📝 Draft pieces:', draftsResponse.pieces?.length || 0);
      console.log('📦 Total created pieces:', allCreatedPieces.length);
      
      // Debug: Check field names and IDs in normalized data
      if (allCreatedPieces.length > 0) {
        console.log('🔍 CREATED - Raw piece from getMarketplacePiecesByCreator:', {
          id: allCreatedPieces[0].id,
          name: allCreatedPieces[0].name,
          mint_address: allCreatedPieces[0].mint_address,
          creator_wallet: allCreatedPieces[0].creator_wallet,
          holder_wallet: allCreatedPieces[0].holder_wallet,
          thumbnailUrl: allCreatedPieces[0].thumbnailUrl,
          previewUrl: allCreatedPieces[0].previewUrl
        });
      }
      
      const userCreatedPieces = allCreatedPieces.map(piece => {
        return {
          ...piece,
          id: piece.id, // Keep numeric database ID for activity/holders queries
          databaseId: piece.id, // Explicit database ID
          name: piece.name,
        description: piece.description,
        category: piece.category || 'other',
        color: piece.color,
        price: piece.price,
        listing_price: piece.listing_price || piece.price,
        // IMPORTANT: Pieces from getMarketplacePiecesByCreator are ALREADY NORMALIZED
        // So we use camelCase field names (previewUrl, thumbnailUrl, posterUrl)
        modelUrl: piece.modelUrl || piece.previewUrl,
        previewUrl: piece.previewUrl || piece.modelUrl,
        thumbnailUrl: piece.thumbnailUrl || '/images/piece-placeholder.png',
        thumbnail_url: piece.thumbnailUrl || '/images/piece-placeholder.png',
        posterUrl: piece.posterUrl,
        poster_url: piece.posterUrl,
        createdDate: piece.created_at,
        createdAt: piece.created_at,
        status: piece.listing_status,
        listing_status: piece.listing_status,
        mint_address: piece.mint_address,
        is_public: piece.is_public,
        is_draft: piece.is_draft,
        isCreated: true,
        inDatabase: true,
        creator: {
          ...piece.creators,
          username: piece.creators?.handle || 'Creator',
          piecesCount: piece.creators?.total_pieces_created || 0,
          verified: piece.creators?.is_verified || false,
          profilePicture: piece.creators?.profile_picture_url,
          profile_picture_url: piece.creators?.profile_picture_url
        },
        originalCreator: {
          ...piece.creators,
          username: piece.creators?.handle || 'Creator',
          piecesCount: piece.creators?.total_pieces_created || 0,
          verified: piece.creators?.is_verified || false,
          profilePicture: piece.creators?.profile_picture_url,
          profile_picture_url: piece.creators?.profile_picture_url
        },
        holder: piece.holder_wallet || piece.creator_wallet || profileWallet,
        creator_wallet: piece.creator_wallet || profileWallet,
        holder_wallet: piece.holder_wallet || piece.creator_wallet || profileWallet
        };
      });
      
      console.log('✅ Final created pieces count:', userCreatedPieces.length);
      
      // Debug: Check created pieces BEFORE grouping
      if (userCreatedPieces.length > 0) {
        console.log('🔍 CREATED - Transformed piece BEFORE grouping:', {
          name: userCreatedPieces[0].name,
          id: userCreatedPieces[0].id,
          databaseId: userCreatedPieces[0].databaseId,
          mint_address: userCreatedPieces[0].mint_address,
          creator_wallet: userCreatedPieces[0].creator_wallet,
          holder_wallet: userCreatedPieces[0].holder_wallet
        });
      }
      
      // Apply grouping logic (just like Explore page)
      console.log('🔗 Applying grouping logic to created pieces...');
      const groupedCreatedPieces = createGroupedPieceCards(userCreatedPieces);
      console.log('✅ Grouped created pieces:', groupedCreatedPieces);
      console.log(`📊 Grouping result: ${userCreatedPieces.length} pieces → ${groupedCreatedPieces.length} cards`);
      
      // Debug: Check created pieces AFTER grouping
      if (groupedCreatedPieces.length > 0) {
        console.log('🔍 CREATED - Grouped piece AFTER grouping:', {
          name: groupedCreatedPieces[0].name,
          id: groupedCreatedPieces[0].id,
          databaseId: groupedCreatedPieces[0].databaseId,
          mint_address: groupedCreatedPieces[0].mint_address,
          _isGroup: groupedCreatedPieces[0]._isGroup,
          _groupSize: groupedCreatedPieces[0]._groupSize,
          _firstPieceId: groupedCreatedPieces[0]._firstPieceId
        });
      }
      
      setCreatedPieces(groupedCreatedPieces);
      setPieces(groupedCreatedPieces);
      setCreatedPiecesLoading(false);
    } catch (error) {
      console.error('❌ Error loading created pieces:', error);
      setCreatedPieces([]);
      setCreatedPiecesLoading(false);
    }
  };

  // OPTIMIZED: Load owned pieces independently (slowest, loaded last)
  const loadOwnedPiecesData = async (userData) => {
    if (!userData) return;
    
    try {
      setOwnedPiecesLoading(true);
      const profileWallet = userData.fullAddress;
      const currentUserWallet = publicKey?.toBase58();
      const isOwnProfile = !username || (profileWallet === currentUserWallet);

      // Load owned pieces: Query database directly (like Created tab does)
      // This ensures all pieces have numeric database IDs for activity/holders
      let userOwnedPieces = [];
      
      // CHANGED: Always load from database for consistent IDs (not from blockchain wallet)
      // This matches how the Created tab works and ensures activity/holders work correctly
      try {
        console.log('🔍 Loading owned pieces from database for:', profileWallet);
        const ownedResponse = await getPiecesByHolder(profileWallet, 0, 1000);
        console.log('Loaded owned pieces from Supabase:', ownedResponse);
        
        const ownedPiecesData = ownedResponse.pieces || [];
        
        // Debug: Check field names and IDs in raw database data
        if (ownedPiecesData.length > 0) {
          console.log('🔍 OWNED - Raw piece from getPiecesByHolder:', {
            id: ownedPiecesData[0].id,
            name: ownedPiecesData[0].name,
            mint_address: ownedPiecesData[0].mint_address,
            creator_wallet: ownedPiecesData[0].creator_wallet,
            holder_wallet: ownedPiecesData[0].holder_wallet,
            thumbnail_url: ownedPiecesData[0].thumbnail_url,
            preview_glb_url: ownedPiecesData[0].preview_glb_url
          });
        }
        
        // Transform owned pieces to match our data structure (EXACTLY like Created tab)
        userOwnedPieces = ownedPiecesData.map(piece => ({
          ...piece,
          id: piece.id, // Keep numeric database ID for activity/holders queries
          databaseId: piece.id, // Explicit database ID
          name: piece.name,
          description: piece.description || 'A premium digital fashion piece',
          category: piece.category || 'other',
          color: piece.color,
          // Use actual purchase price (last_sale_price_lamports) first, then fall back to listing/base price
          price: piece.last_sale_price_lamports 
            ? piece.last_sale_price_lamports / 1_000_000_000 
            : (piece.listing_price || piece.price || 0),
          listing_price: piece.last_sale_price_lamports 
            ? piece.last_sale_price_lamports / 1_000_000_000 
            : (piece.listing_price || piece.price || 0),
          // IMPORTANT: Pieces from getPiecesByHolder are RAW from database
          // So we use snake_case field names (preview_glb_url, thumbnail_url, poster_url)
          modelUrl: piece.preview_glb_url || piece.model_url,
          previewUrl: piece.preview_glb_url || piece.model_url,
          thumbnailUrl: piece.thumbnail_url || '/images/piece-placeholder.png',
          thumbnail_url: piece.thumbnail_url || '/images/piece-placeholder.png',
          posterUrl: piece.poster_url,
          poster_url: piece.poster_url,
          createdDate: piece.created_at,
          createdAt: piece.created_at,
          status: piece.listing_status,
          listing_status: piece.listing_status,
          mint_address: piece.mint_address,
          is_public: piece.is_public,
          is_draft: piece.is_draft,
          isCreated: false, // These are owned, not created
          inDatabase: true,
          // Include creator and holder info (MATCH Created tab format exactly)
          creator: {
            ...piece.creators,
            username: piece.creators?.handle || 'Creator',
            handle: piece.creators?.handle,
            wallet: piece.creator_wallet,
            piecesCount: piece.creators?.total_pieces_created || 0,
            verified: piece.creators?.is_verified || false,
            profilePicture: piece.creators?.profile_picture_url,
            profile_picture_url: piece.creators?.profile_picture_url,
            bio: piece.creators?.bio,
            banner_url: piece.creators?.banner_url,
            social_links: piece.creators?.social_links
          },
          originalCreator: {
            ...piece.creators,
            username: piece.creators?.handle || 'Creator',
            handle: piece.creators?.handle,
            wallet: piece.creator_wallet,
            piecesCount: piece.creators?.total_pieces_created || 0,
            verified: piece.creators?.is_verified || false,
            profilePicture: piece.creators?.profile_picture_url,
            profile_picture_url: piece.creators?.profile_picture_url,
            bio: piece.creators?.bio,
            banner_url: piece.creators?.banner_url,
            social_links: piece.creators?.social_links
          },
          holder: piece.holder_wallet,
          creator_wallet: piece.creator_wallet,
          holder_wallet: piece.holder_wallet
        }));
        
        console.log('✅ Loaded owned pieces from database:', userOwnedPieces.length);
      } catch (error) {
        console.error('❌ Error loading owned pieces from database:', error);
      }
      
      /* OLD CODE - Loading from blockchain wallet (commented out)
      if (isOwnProfile && wallet && isConnected) {
        try {
          console.log('💼 Fetching NFTs from wallet on blockchain...');
          
          // Fetch NFTs from the wallet on-chain
          const allWalletNFTs = await fetchWalletNFTs(wallet);
          console.log(`✅ Found ${allWalletNFTs.length} NFTs in wallet`);
          
          // OPTIMIZED: Limit to first 20 NFTs for faster initial load
          const walletNFTs = allWalletNFTs.slice(0, 20);
          if (allWalletNFTs.length > 20) {
            console.log(`📊 Showing first 20 of ${allWalletNFTs.length} NFTs for performance`);
          }
          
          // AUTO-SYNC: Automatically sync blockchain NFTs to database (limited batch)
          // This allows users to list NFTs they bought/imported from outside the platform
          // Non-blocking - runs in background, doesn't break if it fails
          // OPTIMIZED: Don't await this - let it run in the background!
          import('../utils/autoSyncNFTs').then(({ batchSyncNFTs }) => {
            batchSyncNFTs(walletNFTs, currentUserWallet)
              .then(syncResult => {
                if (syncResult.synced > 0) {
                  console.log(`✅ Auto-synced ${syncResult.synced} new NFTs to database`);
                }
              })
              .catch(syncError => {
                console.warn('⚠️ Auto-sync failed (non-critical):', syncError.message);
              });
          });
          
          // OPTIMIZED: Batch fetch all pieces by mint addresses in ONE query (limited to 20)
          const mintAddresses = walletNFTs.map(nft => nft.mintAddress);
          const { data: piecesInDb, error: batchError } = await supabase
            .from('pieces')
            .select(`
              *,
              social_links,
              creators (
                id,
                wallet,
                handle,
                is_verified,
                profile_picture_url,
                banner_url,
                bio,
                social_links,
                total_pieces_created
              )
            `)
            .in('mint_address', mintAddresses);
          
          if (batchError) {
            console.error('❌ Error batch fetching pieces:', batchError);
          }
          
          // Create a map for O(1) lookup
          const piecesMap = new Map();
          (piecesInDb || []).forEach(piece => {
            piecesMap.set(piece.mint_address, piece);
          });
          
          console.log(`✅ Found ${piecesInDb?.length || 0} pieces in database out of ${walletNFTs.length} NFTs`);
          
          // For each NFT, try to find matching piece in database by mint_address
          const ownedPiecesPromises = walletNFTs.map(async (nft) => {
            try {
              // Look up piece from the batch query
              const piece = piecesMap.get(nft.mintAddress);
              
              if (!piece) {
                // NFT not in our database - show basic info from blockchain
                console.log('⚠️ NFT not found in database:', nft.name);
                console.log('   NFT metadata:', nft.json);
                console.log('   NFT URI:', nft.uri);
                
                // Check for category (case-insensitive)
                const categoryAttrCheck = nft.json?.attributes?.find(attr => 
                  attr.trait_type?.toLowerCase() === 'category'
                );
                console.log('   NFT category from attributes:', categoryAttrCheck?.value);
                
                const thumbnailUrl = nft.json?.image || '/images/piece-placeholder.png';
                const modelUrl = nft.json?.animation_url || null;
                
                // Try to get creator from NFT creators array (convert PublicKey to string)
                const nftCreator = nft.creators && nft.creators.length > 0 ? nft.creators[0] : null;
                const creatorAddressRaw = nftCreator?.address || nft.updateAuthority;
                const creatorAddress = creatorAddressRaw ? 
                  (typeof creatorAddressRaw === 'string' ? creatorAddressRaw : creatorAddressRaw.toString()) : 
                  null;
                const creatorUsername = creatorAddress ? `${creatorAddress.slice(0, 6)}...${creatorAddress.slice(-4)}` : 'Unknown Creator';
                
                // Get category, color, and tags - check case-insensitively
                const categoryAttr = nft.json?.attributes?.find(attr => 
                  attr.trait_type?.toLowerCase() === 'category'
                );
                const colorAttr = nft.json?.attributes?.find(attr => 
                  attr.trait_type?.toLowerCase() === 'color'
                );
                const tagsAttr = nft.json?.attributes?.find(attr => 
                  attr.trait_type?.toLowerCase() === 'tags'
                );
                
                // Parse tags from comma-separated string to array
                const parsedTags = tagsAttr?.value ? 
                  tagsAttr.value.split(',').map(tag => tag.trim()).filter(tag => tag) : 
                  [];
                
                return {
                  id: nft.mintAddress, // Use mint address as ID for blockchain-only pieces
                  databaseId: null, // Not in database, so no database ID
                  mintAddress: nft.mintAddress,
                  mint_address: nft.mintAddress,
                  name: nft.name || 'Unknown NFT',
                  description: nft.json?.description || 'No description available',
                  category: categoryAttr?.value || 'other',
                  color: colorAttr?.value || '',
                  tags: parsedTags,
                  price: 0,
                  listing_price: 0,
                  quantity: 1,
                  thumbnailUrl: thumbnailUrl,
                  thumbnail_url: thumbnailUrl,
                  image: thumbnailUrl,
                  modelUrl: modelUrl,
                  previewUrl: modelUrl,
                  posterUrl: thumbnailUrl,
                  poster_url: thumbnailUrl,
                  listing_status: null,
                  is_public: false,
                  is_draft: false,
                  isCreated: false,
                  inDatabase: false,
                  // Creator info from blockchain
                  creator: {
                    wallet: creatorAddress,
                    username: creatorUsername,
                    handle: creatorUsername,
                    piecesCount: 0
                  },
                  originalCreator: {
                    wallet: creatorAddress,
                    username: creatorUsername,
                    profilePicture: null,
                    piecesCount: 0
                  },
                  // Holder info (current user)
                  holder: currentUserWallet,
                  holder_wallet: currentUserWallet,
                  holderUsername: userData?.username || `${currentUserWallet?.slice(0, 6)}...${currentUserWallet?.slice(-4)}`,
                  // Include NFT metadata
                  nftMetadata: nft
                };
              }
              
              // Check if this piece was created by the current user
              const isCreatedByUser = piece.creator_wallet === currentUserWallet;
              
              console.log(`${isCreatedByUser ? '✅' : '✅'} Including owned NFT:`, piece.name, isCreatedByUser ? '(created by you)' : `by: ${piece.creators?.handle}`);
              console.log('   Database category:', piece.category);
              console.log('   Database tags:', piece.tags);
              
              // Return piece with full database info
              // Use NFT metadata as fallback for images/models if database doesn't have them
              const thumbnailUrl = piece.thumbnail_url || nft.json?.image || '/images/piece-placeholder.png';
              const modelUrl = piece.preview_glb_url || piece.model_url || nft.json?.animation_url;
              
              // Get tags from database first, then fall back to NFT metadata
              let pieceTags = piece.tags || [];
              if (!pieceTags || pieceTags.length === 0) {
                // Try to get from NFT metadata
                const tagsAttr = nft.json?.attributes?.find(attr => 
                  attr.trait_type?.toLowerCase() === 'tags'
                );
                if (tagsAttr?.value) {
                  pieceTags = tagsAttr.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                }
              }
              
              // Get creator info with proper fallbacks
              const creatorInfo = piece.creators || {};
              const creatorUsername = creatorInfo.handle || `Creator ${piece.creator_wallet?.slice(0, 6)}`;
              
              return {
                ...piece,
                id: piece.id, // Keep numeric database ID for activity/holders queries
                databaseId: piece.id, // Explicit database ID
                mintAddress: nft.mintAddress,
                mint_address: piece.mint_address || nft.mintAddress,
                name: piece.name || nft.name,
                description: piece.description || nft.json?.description || 'No description available',
                category: piece.category || 'other',
                color: piece.color || '',
                tags: pieceTags,
                price: piece.price || 0,
                listing_price: piece.listing_price || piece.price || 0,
                quantity: piece.total_made || 1,
                modelUrl: modelUrl,
                previewUrl: modelUrl,
                thumbnailUrl: thumbnailUrl,
                thumbnail_url: thumbnailUrl,
                image: thumbnailUrl,
                posterUrl: piece.poster_url,
                poster_url: piece.poster_url,
                createdDate: piece.created_at,
                createdAt: piece.created_at,
                status: piece.listing_status,
                listing_status: piece.listing_status,
                is_public: piece.is_public,
                is_draft: piece.is_draft,
                isCreated: false,
                inDatabase: true,
                // Properly format creator info (match Created tab structure)
                creator: {
                  ...creatorInfo,
                  username: creatorUsername,
                  handle: creatorInfo.handle || creatorUsername,
                  wallet: piece.creator_wallet,
                  profile_picture_url: creatorInfo.profile_picture_url,
                  profilePicture: creatorInfo.profile_picture_url,
                  piecesCount: creatorInfo.total_pieces_created || 0,
                  verified: creatorInfo.is_verified || false,
                  bio: creatorInfo.bio,
                  banner_url: creatorInfo.banner_url,
                  social_links: creatorInfo.social_links
                },
                originalCreator: {
                  ...creatorInfo,
                  username: creatorUsername,
                  handle: creatorInfo.handle || creatorUsername,
                  wallet: piece.creator_wallet,
                  profilePicture: creatorInfo.profile_picture_url,
                  profile_picture_url: creatorInfo.profile_picture_url,
                  piecesCount: creatorInfo.total_pieces_created || 0,
                  verified: creatorInfo.is_verified || false,
                  bio: creatorInfo.bio,
                  banner_url: creatorInfo.banner_url,
                  social_links: creatorInfo.social_links
                },
                // Properly format holder info (current user)
                holder: currentUserWallet,
                holder_wallet: currentUserWallet,
                creator_wallet: piece.creator_wallet,
                holderUsername: userData?.username || `${currentUserWallet?.slice(0, 6)}...${currentUserWallet?.slice(-4)}`,
                // Include NFT metadata
                nftMetadata: nft
              };
            } catch (err) {
              console.error('Error fetching piece for NFT:', nft.mintAddress, err);
              return null;
            }
          });
          
          // Wait for all pieces to be fetched and filter out nulls
          const resolvedPieces = await Promise.all(ownedPiecesPromises);
          userOwnedPieces = resolvedPieces.filter(piece => piece !== null);
          
          console.log('✅ Final owned pieces from wallet:', userOwnedPieces.length);
          
        } catch (error) {
          console.error('❌ Error loading NFTs from wallet:', error);
        }
      }
      */
      // END OF OLD CODE - Now using database query above for all cases

      // Log final summary
      console.log('📊 OWNED PIECES SUMMARY:');
      console.log('   Owned pieces:', userOwnedPieces.length);
      
      // Debug: Check owned pieces BEFORE grouping
      if (userOwnedPieces.length > 0) {
        console.log('🔍 OWNED - Transformed piece BEFORE grouping:', {
          name: userOwnedPieces[0].name,
          id: userOwnedPieces[0].id,
          databaseId: userOwnedPieces[0].databaseId,
          mint_address: userOwnedPieces[0].mint_address,
          creator_wallet: userOwnedPieces[0].creator_wallet,
          holder_wallet: userOwnedPieces[0].holder_wallet
        });
      }
      
      // NO GROUPING for owned pieces - show each piece separately (like Listings page)
      console.log('📊 Owned pieces (ungrouped):', userOwnedPieces.length);
      
      // Debug: Check owned pieces
      if (userOwnedPieces.length > 0) {
        console.log('🔍 OWNED - Sample piece:', {
          name: userOwnedPieces[0].name,
          id: userOwnedPieces[0].id,
          databaseId: userOwnedPieces[0].databaseId,
          mint_address: userOwnedPieces[0].mint_address
        });
      }
      
      setOwnedPieces(userOwnedPieces);
      setOwnedPiecesLoading(false);
    } catch (error) {
      console.error('❌ Error loading owned pieces:', error);
      setOwnedPieces([]);
      setOwnedPiecesLoading(false);
    }
  };

  // OPTIMIZED: Coordinating function that loads data progressively
  const loadUserData = async () => {
    // Load profile first (fast) - show profile immediately
    const userData = await loadBasicProfile();
    
    if (!userData) {
      return; // If profile loading failed, stop here
    }
    
    // Load all sections in parallel for maximum speed
    // Each section updates its own loading state and data independently
    Promise.all([
      loadCapsulesData(userData),
      loadCreatedPiecesData(userData),
      loadOwnedPiecesData(userData)
    ]).catch(error => {
      console.error('Error loading profile sections:', error);
    });
  };

  const handleShareProfile = () => {
    navigator.clipboard.writeText(window.location.href);
    toast('Profile link copied to clipboard!', 'success');
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(user.fullAddress);
      console.log('Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'website': return <Globe size={16} />;
      case 'x': 
      case 'twitter': return <span className="text-gray-400 group-hover:text-white font-bold text-sm">𝕏</span>;
      case 'instagram': return <Instagram size={16} />;
      default: return <Globe size={16} />;
    }
  };

  const getSocialLabel = (platform) => {
      switch (platform) {
      case 'website': return 'Website';
      case 'x': 
      case 'twitter': return 'X (Twitter)';
      case 'instagram': return 'Instagram';
      default: return 'Website';
    }
  };

  const formatSocialUrl = (url, platform) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
    }
    return `https://${url}`;
  };

  // Filter items based on search term and current view
  const getFilteredItems = () => {
    let itemsToFilter = [];
    
    if (currentView === 'created') {
      itemsToFilter = createdPieces;
    } else if (currentView === 'owned') {
      itemsToFilter = ownedPieces;
    } else if (currentView === 'capsules') {
      return capsules; // Capsules don't need the same filtering
    } else {
      itemsToFilter = pieces; // Fallback for other views
    }
    
    console.log(`📊 Filtering ${currentView} tab:`, {
      totalItems: itemsToFilter.length,
      sortBy,
      searchTerm: searchTerm || '(none)'
    });
    
    // Filter by search term
    let filtered = itemsToFilter;
    if (searchTerm.trim()) {
      filtered = itemsToFilter.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      // Parse price - handle both string and number formats
      const getPriceValue = (item) => {
        const price = item.listing_price || item.price || 0;
        if (typeof price === 'string') {
          return parseFloat(price.replace(/[^0-9.]/g, '')) || 0;
        }
        return parseFloat(price) || 0;
      };
      
      const priceA = getPriceValue(a);
      const priceB = getPriceValue(b);
      
      // Debug logging for first few items
      if (filtered.length <= 5) {
        console.log(`🔢 Sorting ${currentView}:`, {
          sortBy,
          itemA: { name: a.name, price: a.price, priceValue: priceA },
          itemB: { name: b.name, price: b.price, priceValue: priceB }
        });
      }
      
      switch (sortBy) {
        case 'price-low':
          return priceA - priceB;
        case 'price-high':
          return priceB - priceA;
        case 'recent':
          return new Date(b.createdDate || b.createdAt || 0) - new Date(a.createdDate || a.createdAt || 0);
        case 'sold':
          // For now, sort by random since we don't have sold date
          // In a real app, you'd sort by actual sold date
          return new Date(b.createdDate || b.createdAt || 0) - new Date(a.createdDate || a.createdAt || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  };

  // Handle buy now button
  const handleBuyNow = withWalletCheck((item) => {
    // Check if the item is actually for sale
    if (item.status !== 'listed') {
      setNotForSaleModal({ isOpen: true, item });
      return;
    }
    setBuyModal({ isOpen: true, item });
  }, 'make a purchase');

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request submission
  const handleTradeRequestSubmit = (tradeRequest) => {
    console.log('Trade request submitted:', tradeRequest);
    // Close both the trade modal and the piece detail modal
    setRequestTradeModal({ isOpen: false, item: null });
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    console.log('Trade request sent successfully');
    toast.success(message || 'Trade request sent successfully! 🎉');
  };

  // Handle piece detail modal
  const openPieceDetailModal = (item) => {
    console.log('Opening piece detail modal for:', item);
    console.log('Item data:', item);
    console.log('Item thumbnailUrl:', item?.thumbnailUrl);
    console.log('Item modelUrl:', item?.modelUrl);
    console.log('Item previewUrl:', item?.previewUrl);
    console.log('Item modelFile:', item?.modelFile);
    console.log('🔍 Piece data check:', {
      id: item?.id,
      databaseId: item?.databaseId,
      mint_address: item?.mint_address,
      creator: item?.creator,
      originalCreator: item?.originalCreator,
      creator_wallet: item?.creator_wallet,
      holder_wallet: item?.holder_wallet,
      isCreated: item?.isCreated
    });
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      console.log('  - First piece in group:', {
        id: item._groupPieces?.[0]?.id,
        databaseId: item._groupPieces?.[0]?.databaseId,
        mint_address: item._groupPieces?.[0]?.mint_address,
        inDatabase: item._groupPieces?.[0]?.inDatabase
      });
      console.log('  - All piece IDs in group:', item._groupPieces?.map(p => ({ 
        id: p.id, 
        databaseId: p.databaseId,
        mint: p.mint_address
      })));
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedItem = item ? normalizePieces([item])[0] : item;
    setPieceDetailModal({ isOpen: true, item: normalizedItem });
    
    // Check if piece has 3D model - check multiple sources
    const has3DModel = !!(
      item?.modelUrl && (
        item.modelUrl.includes('.glb') || 
        item.modelUrl.includes('.gltf') ||
        item.modelUrl.includes('data:model/') ||
        item.modelUrl.includes('blob:')
      )
    );
    
    const hasPreviewUrl = !!(
      item?.previewUrl && (
        item.previewUrl.includes('.glb') || 
        item.previewUrl.includes('.gltf') ||
        item.previewUrl.includes('data:model/') ||
        item.previewUrl.includes('blob:')
      )
    );
    
    const hasModelFile = !!(
      item?.modelFile?.dataUrl && (
        item.modelFile.dataUrl.includes('.glb') || 
        item.modelFile.dataUrl.includes('.gltf') ||
        item.modelFile.type === 'model/gltf-binary' ||
        item.modelFile.type === 'model/gltf+json'
      )
    );
    
    const hasAny3DModel = has3DModel || hasPreviewUrl || hasModelFile;
    
    console.log('3D Model check:', {
      has3DModel,
      hasPreviewUrl,
      hasModelFile,
      hasAny3DModel,
      itemPreviewUrl: item?.previewUrl,
      itemModelUrl: item?.modelUrl,
      itemModelFile: item?.modelFile?.dataUrl
    });
    
    if (hasAny3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
      console.log('3D model detected, setting view mode to 3d');
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
      console.log('No 3D model detected, setting view mode to thumbnail');
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle favorite toggle (now using the useFavorites hook)
  const handleToggleFavorite = (item) => {
    console.log('Toggle favorite for item:', item.name);
    toggleFavorite(item.id);
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle holder button click - navigate to holder profile
  const handleHolderClick = (item) => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      toast('Wallet not connected', 'error');
      return;
    }

    const { item } = buyModal;
    const price = parseFloat(item.price);
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      toast(`Not enough SOL. You need ${balanceCheck.required.toFixed(2)} SOL but only have ${balanceCheck.balance.toFixed(2)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      
      const result = await completePurchaseWithDelegate(
        { connected: isConnected, publicKey, sendTransaction: window.solana?.signAndSendTransaction || window.solflare?.signAndSendTransaction },
        item,
        price
      );
      
      // Show success message with transaction details
      const nftInfo = result.nftTransaction ? 
        `\n\n🎨 NFT Transferred: ${result.nftTransaction.signature.slice(0, 8)}...` : 
        '\n\nℹ️ Piece not minted on-chain yet';
      
      // Build royalty info if applicable
      const royaltyInfo = result.feeBreakdown.royaltyAmount > 0
        ? ` | Creator Royalty (${result.feeBreakdown.royaltyPercentage.toFixed(1)}%): ${result.feeBreakdown.royaltyAmountFormatted} SOL`
        : '';
      
      toast(
        `Successfully purchased ${item.name}! 🎉`,
        'success'
      );
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // Reload pieces to update the UI
      loadPieces();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      // Make error messages user-friendly
      let userMessage = 'Unable to complete purchase. Please try again.';
      if (error.message?.includes('insufficient')) {
        userMessage = 'Not enough SOL in your wallet to complete this purchase.';
      } else if (error.message?.includes('rejected') || error.message?.includes('declined')) {
        userMessage = 'Purchase cancelled. You declined the transaction.';
      } else if (error.message?.includes('delegate') || error.message?.includes('delegated')) {
        userMessage = 'Unable to complete purchase. The item may no longer be available.';
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      toast(userMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck((item) => {
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7 });
  }, 'make an offer');

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      toast('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (isNaN(offerPrice) || offerPrice <= 0) {
      toast('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      toast(`Not enough SOL. You need ${offerPrice} SOL but only have ${balance.toFixed(2)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      toast('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      console.log(`Piece ID: ${item.id} -> ${realPieceId}`);
      console.log(`Expires in: ${expirationDays || 7} days`);
      console.log(`From wallet: ${publicKey.toString()}`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays || 7,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          toast.error('You already have an active offer on this piece. Visit the Offers page to view or update it.');
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      console.log('Offer ID:', result.offer.id);
      
      // Show success notification
      toast.success(`Offer of ${offerPrice} SOL submitted successfully! 🎉`);
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  };

  // Image upload functions
  const handleImageUpload = async (file, type) => {
    if (!file || !publicKey) return;
    
    try {
      setIsProcessing(true);
      const walletAddress = publicKey.toString();
      const imageUrl = await uploadProfileImage(file, walletAddress, type);
      
      if (type === 'profilePicture') {
        setEditForm(prev => ({ ...prev, profilePicture: imageUrl }));
      } else if (type === 'banner') {
        setEditForm(prev => ({ ...prev, banner: imageUrl }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast('Failed to upload image. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveImage = (type) => {
    if (type === 'profilePicture') {
      setEditForm(prev => ({ ...prev, profilePicture: null }));
    } else if (type === 'banner') {
      setEditForm(prev => ({ ...prev, banner: null }));
    }
  };

  // Edit profile functions
  const handleEditProfile = () => {
    if (!username && isConnected) {
      // Only allow editing for current user's profile
      setEditForm({
        username: user?.username || '',
        bio: user?.bio || '',
        profilePicture: user?.profilePicture || null,
        banner: user?.banner || null,
        socialLinks: {
          twitter: user?.socialLinks?.twitter || '',
          instagram: user?.socialLinks?.instagram || '',
          website: user?.socialLinks?.website || ''
        }
      });
      setIsEditing(true);
    }
  };

  const handleSaveProfile = () => {
    setShowSaveConfirm(true);
  };

  const confirmSaveProfile = async () => {
    if (!user || !publicKey) return;
    
    try {
      setIsProcessing(true);
      const walletAddress = publicKey.toString();
      
      // Prepare profile data for Supabase
      const profileData = {
        handle: editForm.username,
        bio: editForm.bio,
        profile_picture_url: editForm.profilePicture,
        banner_url: editForm.banner,
        social_links: editForm.socialLinks
      };
      
      // Update profile in Supabase
      const result = await updateCreatorProfile(walletAddress, profileData);
      
      if (!result.success) {
        // Handle specific error codes with friendly messages
        if (result.code === 'HANDLE_CONFLICT') {
          toast.warning(result.error);
        } else if (result.code === 'VALIDATION_ERROR') {
          toast.error(result.error);
        } else {
          toast.error(`Failed to update profile: ${result.error}`);
        }
        // Don't close the edit form on validation errors so user can fix them
        setShowSaveConfirm(false);
        return;
      }
      
      // Update local user state
      const updatedUser = {
        ...user,
        username: editForm.username,
        bio: editForm.bio,
        profilePicture: editForm.profilePicture,
        banner: editForm.banner,
        socialLinks: editForm.socialLinks
      };
      setUser(updatedUser);
      setIsEditing(false);
      setShowSaveConfirm(false);
      
      console.log('Profile updated successfully:', result.data);
      toast.success('Profile updated successfully!');
      
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelSaveProfile = () => {
    setShowSaveConfirm(false);
  };

  const handleCancelEdit = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelEdit = () => {
    setIsEditing(false);
    setShowCancelConfirm(false);
    setEditForm({
      username: '',
      bio: '',
      profilePicture: null,
      banner: null,
      socialLinks: {
        twitter: '',
        instagram: '',
        website: ''
      }
    });
  };

  const cancelCancelEdit = () => {
    setShowCancelConfirm(false);
  };


  if (loading) {
      return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading profile...</div>
        </div>
      );
    }

  // If viewing own profile without wallet connection, require wallet (check this BEFORE user not found)
  if (!username && !isConnected) {
    return (
      <WalletRequired 
        message="You need to connect your wallet to view your profile and manage your pieces."
        action="access your profile"
      />
    );
  }

  if (!user) {
      return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h1 className="text-2xl font-bold text-white mb-2">User Not Found</h1>
          <p className="text-gray-400 mb-6">This user doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/explore')}
            className="px-6 py-3 bg-white hover:bg-gray-200 text-gray-900 rounded-xl font-medium transition-colors"
          >
            Back to Explore
          </button>
          </div>
        </div>
      );
    }

  return (
    <div className="min-h-screen bg-black">
      {/* Banner Section */}
      <div className="relative">
        <div className="aspect-[1400/400] bg-black flex items-center justify-center overflow-hidden">
          {isEditing ? (
            <div className="relative w-full h-full">
              {editForm.banner ? (
                <img 
                  src={editForm.banner} 
                  alt="Profile Banner" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-purple-900 via-purple-800 to-blue-800"></div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="text-center">
                  <label className="inline-flex items-center gap-2 px-4 md:px-6 py-3 md:py-3 bg-[#1a1a1a] border-2 border-white/40 hover:bg-[#2a2a2a] hover:border-white text-white rounded-xl cursor-pointer transition-all duration-200 font-semibold shadow-xl text-sm md:text-base">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'banner')}
                      className="hidden"
                    />
                    <Camera size={18} className="md:w-[20px] md:h-[20px]" />
                    <span className="hidden sm:inline">{editForm.banner ? 'Change Banner' : 'Upload Banner'}</span>
                    <span className="sm:hidden">Upload Banner</span>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            user.banner ? (
              <img 
                src={user.banner} 
                alt="Profile Banner" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-purple-900 via-purple-800 to-blue-800"></div>
            )
          )}
          
          {/* Action Buttons - Bottom Right */}
          {!isEditing && (
            <div className="absolute bottom-2 right-2 md:bottom-4 md:right-4 flex space-x-1 md:space-x-2 z-20">
              {username && (
                <button 
                  onClick={() => toggleWalletFavorite(user.id, {
                    id: user.id,
                    username: user.username,
                    walletAddress: user.fullAddress || user.address,
                    piecesCount: user.stats?.totalPieces || 0,
                    followers: user.stats?.followers || 0,
                    following: user.stats?.following || 0,
                    verified: user.verified || false,
                    verificationType: user.verificationType || 'none',
                    avatar: user.profilePicture,
                    bio: user.bio || '',
                    joinedDate: user.joinDate,
                    totalVolume: user.stats?.totalValue || 0,
                    avgPrice: 0
                  })}
                  className="p-2 rounded-lg transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg relative z-10 bg-black/60"
                  title={isWalletFavorited(user.id) ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart 
                    size={20} 
                    className={`${
                      isWalletFavorited(user.id) 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-white/70 hover:text-red-500'
                    } transition-all duration-300`} 
                  />
                </button>
              )}
              <button 
                onClick={handleCopyAddress}
                className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
                title="Copy wallet address"
              >
                <Copy size={20} />
              </button>
              <button 
                onClick={handleShareProfile}
                className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
                title="Share profile"
              >
                <Share2 size={20} />
              </button>
              {!username && isConnected && (
                <button 
                  onClick={handleEditProfile}
                  className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
                  title="Edit profile"
                >
                  <Edit size={20} />
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Creator Info Overlay */}
        <div className="absolute bottom-2 left-4 right-4">
          <div className={`rounded-xl p-4 ${isEditing ? 'bg-black border-2 border-white/20' : 'bg-black/70'}`}>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <div className="relative">
                    <img
                      src={editForm.profilePicture || '/images/piece-placeholder.png'}
                      alt={editForm.username || 'Profile'}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center border-2 border-white/30">
                      <label className="p-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-white/40 text-white rounded-full cursor-pointer transition-all duration-200 shadow-lg">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e.target.files[0], 'profilePicture')}
                          className="hidden"
                        />
                        <Camera size={16} />
                      </label>
                    </div>
                  </div>
                ) : (
                  <img
                    src={user.profilePicture || '/images/piece-placeholder.png'}
                    alt={user.username || 'Profile'}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isEditing ? (
                      <>
                        {/* Desktop: Show input field */}
                        <input
                          type="text"
                          value={editForm.username}
                          onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                          className="hidden md:block w-full max-w-xs text-white font-semibold bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 text-sm placeholder-gray-400"
                          placeholder="Enter nickname"
                        />
                        {/* Mobile: Show display name */}
                        <span className="md:hidden text-white font-semibold text-sm">
                          {editForm.username || user.username || 'Anonymous User'}
                        </span>
                      </>
                    ) : (
                      <span className="text-white font-semibold">
                        {user.username || 'Anonymous User'}
                      </span>
                    )}
                    {user.verified && (
                      <VerifiedBadge 
                        size="md" 
                        verificationType={user.verificationType || 'sales_milestone'}
                      />
                    )}
                    {!username && isConnected && verificationStatus.status === 'pending' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs rounded-full">
                        <Clock size={10} />
                        Pending
                      </div>
                    )}
                    {!username && isConnected && verificationStatus.status === 'rejected' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-red-600/20 text-red-400 text-xs rounded-full">
                        <X size={10} />
                        Rejected
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    // Hide bio textarea on mobile in overlay, show on desktop
                    <div className="w-full hidden md:block">
                      <textarea
                        value={editForm.bio}
                        onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                        maxLength={50}
                        className="w-full mt-1 bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 resize-none"
                        placeholder="Tell us about yourself"
                        rows={2}
                      />
                      <div className="text-right text-xs text-gray-400 mt-1">
                        {editForm.bio.length}/50
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-300 text-sm">{user.bio}</p>
                  )}
                </div>
              </div>
              
              {/* Social Links */}
              {!isEditing && Object.values(user.socialLinks || {}).some(link => link && link.trim()) && (
                <div className="flex gap-2 items-center">
                  {Object.entries(user.socialLinks || {}).map(([platform, url]) => {
                    if (!url || !url.trim()) return null;
                    const formattedUrl = formatSocialUrl(url, platform);
                    return (
                      <a
                        key={platform}
                        href={formattedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                        title={getSocialLabel(platform)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                          {getSocialIcon(platform)}
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="p-6">
        {/* Edit Mode - Username and Bio (Mobile Only) */}
        {isEditing && (
          <div className="md:hidden mb-6 w-full">
            <div className="bg-[#0a0a0a] border-2 border-white/20 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 text-base">Profile Info</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 font-medium mb-2 text-sm">Username</label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full text-white font-semibold bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2.5 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 text-sm placeholder-gray-400"
                    placeholder="Enter nickname"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-medium mb-2 text-sm">Bio</label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                    maxLength={50}
                    className="w-full bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 resize-none"
                    placeholder="Tell us about yourself"
                    rows={3}
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {editForm.bio.length}/50
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Mode - Social Links */}
        {isEditing && (
          <div className="mb-6 w-full max-w-2xl">
            <div className="bg-[#0a0a0a] border-2 border-white/20 rounded-xl p-4 md:p-6">
              <h3 className="text-white font-semibold mb-4 text-base md:text-lg">Social Links</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 font-medium mb-2 text-sm">X (Twitter)</label>
                  <input
                    type="url"
                    value={editForm.socialLinks.twitter}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      socialLinks: { ...prev.socialLinks, twitter: e.target.value }
                    }))}
                    className="w-full bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 text-sm"
                    placeholder="https://x.com/yourusername"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-medium mb-2 text-sm">Instagram</label>
                  <input
                    type="url"
                    value={editForm.socialLinks.instagram}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      socialLinks: { ...prev.socialLinks, instagram: e.target.value }
                    }))}
                    className="w-full bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 text-sm"
                    placeholder="https://instagram.com/yourusername"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-medium mb-2 text-sm">Website</label>
                  <input
                    type="url"
                    value={editForm.socialLinks.website}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      socialLinks: { ...prev.socialLinks, website: e.target.value }
                    }))}
                    className="w-full bg-[#1a1a1a] border-2 border-white/30 rounded-lg px-3 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/50 text-sm"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Action Buttons */}
        {isEditing && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mb-6">
            <button
              onClick={handleCancelEdit}
              className="px-6 py-3 bg-[#1a1a1a] border-2 border-white/30 hover:bg-[#2a2a2a] hover:border-white/50 text-white rounded-lg transition-all duration-200 font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 border-2 border-green-500 text-white rounded-lg transition-all duration-200 font-semibold flex items-center justify-center gap-2 shadow-lg"
            >
              <Check size={18} />
              Save Changes
            </button>
          </div>
        )}

        {/* Storefront Content */}
        <div className="space-y-6">
          {/* Navigation Tabs */}
          <div className="border-b border-[#2a2a2a] overflow-x-auto scrollbar-hide">
            <nav className="flex space-x-4 md:space-x-8 min-w-max md:min-w-0">
              <button 
                onClick={() => setCurrentView('created')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'created' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Created ({createdPieces.length})
              </button>
              <button 
                onClick={() => setCurrentView('capsules')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'capsules' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Capsules ({capsules.length})
              </button>
              <button 
                onClick={() => setCurrentView('owned')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'owned' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Owned ({ownedPieces.length})
              </button>
            </nav>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 px-4 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:border-white"
                />
              </div>
              {/* Sort Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300 hidden sm:inline">Sort by:</span>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === sortBy
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === sortBy && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
          </div>

          {/* Content Display */}
          <div className="space-y-6">
            <div className="px-3 md:px-6 py-4 mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                {currentView === 'created' ? 'Created Pieces' :
                 currentView === 'owned' ? 'Owned Pieces' :
                 currentView === 'capsules' ? 'Capsules' : 
                 currentView.charAt(0).toUpperCase() + currentView.slice(1)}
              </h2>
              <p className="text-gray-400 text-sm">
                {getFilteredItems().length} {currentView === 'capsules' ? 'capsules' : 'pieces'}
              </p>
          </div>

          {(currentView === 'created' || currentView === 'owned') && (
              <div>
                {/* Show loading skeleton while data is loading */}
                {((currentView === 'created' && createdPiecesLoading) || (currentView === 'owned' && ownedPiecesLoading)) ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="bg-[#1a1a1a] border border-white/20 rounded-xl overflow-hidden animate-pulse">
                        <div className="aspect-square bg-gray-800"></div>
                        <div className="p-4">
                          <div className="h-4 bg-gray-800 rounded mb-2"></div>
                          <div className="h-3 bg-gray-800 rounded w-2/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : getFilteredItems().length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
                      {getFilteredItems().map((item) => {
                        // Check if current user owns this piece
                        const isOwner = isConnected && publicKey && 
                          item?.holder_wallet === publicKey.toString();
                        
                        // Determine if piece is owned by creator (primary sale)
                        const creatorWallet = item?.creator_wallet || 
                                            item?.creators?.wallet ||
                                            item?.originalCreator?.wallet;
                        const isOwnedByCreator = item?.holder_wallet === creatorWallet;
                        
                        // Profile page is VIEW-ONLY - no buy/sell/trade actions
                        // All transactions should happen from Listings or Explore pages
                        
                        return (
                          <UnifiedPieceCard
                            key={item.id}
                            item={{
                              ...item,
                              price: typeof item.price === 'string' ? item.price.replace(' SOL', '') : item.price, // Remove ' SOL' suffix if present and price is string
                              // Don't override thumbnailUrl - it's already set in the data transformation above
                            }}
                            onClick={openPieceDetailModal}
                            onBuy={() => {}} // No-op
                            onTrade={() => {}} // No-op
                            onFavorite={(item) => toggleFavorite(item.id)}
                            isFavorited={isFavorited(item.id)}
                            solPrice={solPrice}
                            showBuyButton={false} // Hide buy button - Profile is view-only
                            showTradeButton={false} // Hide trade button - Profile is view-only
                            showFavoriteButton={!!username} // Show favorite button only when viewing someone else's profile
                            showEditionBadges={currentView === 'created'} // Show edition badges only on created tab
                            buyButtonText="Buy now"
                            tradeButtonText="Trade"
                          />
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">
                      {currentView === 'created' ? 'No created pieces yet' : 'No owned pieces yet'}
                    </h3>
                    <p className="text-gray-500 mb-6">
                      {currentView === 'created' 
                        ? 'This user hasn\'t created any pieces yet' 
                        : 'This user doesn\'t own any pieces yet'}
                    </p>
                  </div>
                )}
            </div>
          )}

          {currentView === 'capsules' && (
              <div>
                {/* Show loading skeleton while capsules are loading */}
                {capsulesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="bg-[#1a1a1a] border border-white/20 rounded-xl overflow-hidden animate-pulse">
                        <div className="aspect-video bg-gray-800"></div>
                        <div className="p-6">
                          <div className="h-5 bg-gray-800 rounded mb-3"></div>
                          <div className="h-3 bg-gray-800 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-800 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : capsules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {capsules.map((capsule) => (
                <div 
                  key={capsule.id}
                  className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20"
                >
                  <div 
                    onClick={() => navigate(`/capsule/${capsule.id}`)}
                    className="cursor-pointer"
                  >
                  {/* Banner Image - Full width, no padding */}
                  <div className="relative aspect-[1400/400] bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                    {capsule.banner ? (
                      <img 
                        src={capsule.banner} 
                        alt={capsule.name}
                        className="w-full h-full object-cover"
                      />
                    ) : capsule.image ? (
                      <img 
                        src={capsule.image} 
                        alt={capsule.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-4xl opacity-30">📦</div>
                    )}
                  </div>

                  {/* Bottom Section with Info */}
                  <div className="p-3 space-y-2">
                    {/* Title with Number */}
                    <h3 className="text-white font-bold text-sm line-clamp-1">
                      {capsule.name} {capsule.capsuleNumber}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-400 text-xs line-clamp-2">
                            {capsule.description || (() => {
                              const count = capsule.pieceCount || capsule.piece_count || capsule.collectionModels?.length || 0;
                              return `A curated collection of ${count} digital fashion ${count === 1 ? 'piece' : 'pieces'} with modern styling and premium quality.`;
                            })()}
                    </p>

                    {/* Stats Row */}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                                {(() => {
                                  const count = capsule.pieceCount || capsule.piece_count || capsule.collectionModels?.length || 0;
                                  return `${count} ${count === 1 ? 'piece' : 'pieces'}`;
                                })()}
                        </span>
                      </div>
                    </div>

                    {/* Price Row */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-400">Avg Price</div>
                        <div className="text-green-400 font-semibold text-sm">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                // Show more precision for prices < 0.1 SOL
                                if (price > 0 && price < 0.1) {
                                  return price.toFixed(3);
                                }
                                return isNaN(price) ? '0.0' : price.toFixed(1);
                              })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-400">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                const usdPrice = isNaN(price) ? 0 : price * solPrice;
                                return usdPrice > 0 ? `~$${usdPrice.toFixed(2)} USD` : '';
                              })()}
                      </div>
                    </div>

                    {/* Drop Date */}
                    <div className="text-xs text-gray-500">
                      Dropped {capsule.dropDate ? new Date(capsule.dropDate).toLocaleDateString('en-US', { 
                        year: '2-digit', 
                        month: '2-digit', 
                        day: '2-digit' 
                            }) : capsule.createdAt ? new Date(capsule.createdAt).toLocaleDateString('en-US', { 
                        year: '2-digit', 
                        month: '2-digit', 
                        day: '2-digit' 
                      }) : 'Recently'}
                    </div>
                  </div>
                  </div>
                  
                  {/* Heart Icon */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCapsuleFavorite(capsule);
                    }}
                    className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                  >
                    <Heart 
                      size={16} 
                      className={`${
                        isCapsuleFavorited(capsule.id) 
                          ? 'text-red-500 fill-red-500' 
                          : 'text-white/70 hover:text-red-500'
                      } transition-all duration-300`} 
                    />
                  </button>
                </div>
              ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">No capsules yet</h3>
                    <p className="text-gray-500 mb-6">This user hasn't created any capsules yet</p>
                  </div>
                )}
            </div>
          )}

        </div>
      </div>
      </div>


      {/* Grouped Pieces Modal */}
      {groupedPiecesModal.isOpen && (
        <GroupedPiecesModal
          isOpen={groupedPiecesModal.isOpen}
          onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
          groupedCard={groupedPiecesModal.groupedCard}
          solPrice={solPrice || 195.84}
          wallet={wallet}
          publicKey={publicKey}
          isConnected={isConnected}
          onBuyNow={(selectedPiece) => {
            // Trigger buy flow for selected piece
            handleBuyNow(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
          onMakeOffer={(selectedPiece) => {
            // Trigger offer flow for selected piece
            handleMakeOffer(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
          onRequestTrade={(selectedPiece) => {
            // Trigger trade flow for selected piece
            handleRequestTrade(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
        />
      )}

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - 3D Model */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  // Also check for modelFile with 3D model data
                  const hasModelFile = pieceDetailModal.item?.modelFile?.dataUrl && (
                    pieceDetailModal.item.modelFile.dataUrl.includes('.glb') || 
                    pieceDetailModal.item.modelFile.dataUrl.includes('.gltf') ||
                    pieceDetailModal.item.modelFile.type === 'model/gltf-binary' ||
                    pieceDetailModal.item.modelFile.type === 'model/gltf+json'
                  );
                  
                  // Check for modelUrl and previewUrl
                  const hasModelUrl = pieceDetailModal.item?.modelUrl && (
                    pieceDetailModal.item.modelUrl.includes('.glb') || 
                    pieceDetailModal.item.modelUrl.includes('.gltf') ||
                    pieceDetailModal.item.modelUrl.includes('data:model/') ||
                    pieceDetailModal.item.modelUrl.includes('blob:')
                  );
                  
                  const hasPreviewUrl = pieceDetailModal.item?.previewUrl && (
                    pieceDetailModal.item.previewUrl.includes('.glb') || 
                    pieceDetailModal.item.previewUrl.includes('.gltf') ||
                    pieceDetailModal.item.previewUrl.includes('data:model/') ||
                    pieceDetailModal.item.previewUrl.includes('blob:')
                  );
                  
                  if (has3DModel || hasModelFile || hasModelUrl || hasPreviewUrl) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square">
                  {(() => {
                    if (modalViewMode === '3d') {
                      // Show 3D model
                      const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                      const modelBucket = normalizedPiece?.previewBucket || normalizedPiece?.modelBucket;
                      const modelKey = normalizedPiece?.previewKey || normalizedPiece?.modelKey;
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      // Check for modelFile data
                      const modelFileSrc = pieceDetailModal.item?.modelFile?.dataUrl && (
                        pieceDetailModal.item.modelFile.dataUrl.includes('.glb') || 
                        pieceDetailModal.item.modelFile.dataUrl.includes('.gltf') ||
                        pieceDetailModal.item.modelFile.type === 'model/gltf-binary' ||
                        pieceDetailModal.item.modelFile.type === 'model/gltf+json'
                      ) ? pieceDetailModal.item.modelFile.dataUrl : null;
                      
                      if (modelBucket && modelKey) {
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc || modelFileSrc) {
                        const srcToUse = modelFileSrc || fallbackSrc;
                        return (
                          <ModelViewer
                            src={srcToUse}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : pieceDetailModal.item?.modelFile?.dataUrl && pieceDetailModal.item.modelFile.dataUrl.startsWith('data:image/') ? (
                        <img 
                          src={pieceDetailModal.item.modelFile.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Original Creator */}
                <div>
                  <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                    Original Creator
                  </div>
                  <button 
                    onClick={() => handleAccountClick(pieceDetailModal.item)}
                    className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                      {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                        <img
                          src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                          alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                          {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                        </div>
                        {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                          <VerifiedBadge 
                            size="sm" 
                            verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'sales_milestone'}
                          />
                        )}
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                      </div>
                    </div>
                    <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                  </button>

                  {/* Creator Royalty - Outside button */}
                  {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                    <div className="mt-3 px-1">
                      <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                        {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                      </span>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                {(() => {
                  // Use piece social links
                  const socialLinks = pieceDetailModal.item?.social_links || {};
                  const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                  
                  // DEBUG: Log social links data
                  console.log('🔍 [PROFILE] Social Links Debug:', {
                    pieceId: pieceDetailModal.item?.id,
                    pieceName: pieceDetailModal.item?.name,
                    social_links: socialLinks,
                    hasSocialLinks
                  });
                  
                  if (!hasSocialLinks) return null;
                  
                  return (
                    <div className="flex items-center gap-2 mt-3">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                    </div>
                    {/* Tags from minting */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 && pieceDetailModal.item.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                        activityPanelOpen 
                          ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                          : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                      }`}
                    >
                      Activity
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm">
                          {getCategoryDisplayName(pieceDetailModal.item?.category) || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <h3 className="text-white font-semibold mb-2">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'No description available'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <h3 className="text-white font-semibold mb-3">Pricing</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Listed Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            return displayPrice;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          return ((parseFloat(displayPrice) || 0) * solPrice).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm">
                    Dropped {pieceDetailModal.item?.createdDate ? new Date(pieceDetailModal.item.createdDate).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Owner Action Buttons */}
                  <div className="space-y-3 pt-2">
                    {(() => {
                      // Check if current user owns this piece
                      const isOwnedByCurrentUser = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      // Check if piece is in database
                      const hasValidDatabaseId = typeof pieceDetailModal.item?.id === 'number';
                      const isExternalNFT = pieceDetailModal.item?.inDatabase === false || !hasValidDatabaseId;
                      
                      console.log('🔍 Modal button check:', {
                        isConnected,
                        hasPublicKey: !!publicKey,
                        publicKey: publicKey?.toString(),
                        holderWallet: pieceDetailModal.item?.holder_wallet,
                        isOwnedByCurrentUser,
                        inDatabase: pieceDetailModal.item?.inDatabase,
                        id: pieceDetailModal.item?.id,
                        hasValidDatabaseId,
                        isExternalNFT,
                        itemData: pieceDetailModal.item
                      });
                      
                      // Profile is view-only - redirect to appropriate page for actions
                      if (isOwnedByCurrentUser) {
                        const isListed = pieceDetailModal.item?.listing_status === 'listed';
                        
                        return (
                          <>
                            {/* Ownership Badge */}
                            <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30">
                              <span className="text-sm font-medium">✓ You own this piece</span>
                            </div>
                            {!isListed && (
                              <button 
                                onClick={() => navigate('/listings')}
                                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
                              >
                                <Tag size={16} />
                                List on Listings Page
                              </button>
                            )}
                          </>
                        );
                      } else {
                        return (
                          <button 
                            onClick={() => navigate('/explore')}
                            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
                          >
                            View on Explore Page
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.databaseId || pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{buyModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Price</span>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-lg">{buyModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(buyModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0')}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it. You can cancel your offer at any time.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Edit Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <X size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Discard Changes?</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to discard your changes?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="text-gray-300 text-sm">
                Any unsaved changes to your profile will be lost. This action cannot be undone.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={cancelCancelEdit}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Keep Editing
              </button>
              <button
                onClick={confirmCancelEdit}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Changes Confirmation Modal */}
      {showSaveConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Check size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Save Changes?</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to save your changes?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="text-gray-300 text-sm">
                Your profile will be updated with the new information. This action will be saved permanently.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={cancelSaveProfile}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Keep Editing
              </button>
              <button
                onClick={confirmSaveProfile}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={handleTradeRequestSubmit}
        onSuccess={handleTradeRequestSuccess}
      />

      {/* Not For Sale Modal */}
      {notForSaleModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={32} className="text-red-500" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-white mb-2">
                Not Available for Purchase
              </h3>
              
              {/* Message */}
              <p className="text-gray-400 mb-6 leading-relaxed">
                This piece is not currently for sale. The holder hasn't listed it for sale, but you can make an offer or request a trade!
              </p>
              
              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setNotForSaleModal({ isOpen: false, item: null })}
                  className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Notification */}
      {showVerificationNotification && (
        <VerificationNotification
          verificationStatus={verificationStatus}
          onClose={() => setShowVerificationNotification(false)}
        />
      )}

      {/* Wallet Connection Modal */}
      <WalletConnectionModal
        isOpen={showWalletModal}
        onClose={closeWalletModal}
        message={walletModalMessage}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Profile; 